import React, { useState } from 'react';
import './WhoWeAre.css';

const WhoWeAre = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const scrollToSection = (sectionId) => {
    setActiveTab(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="who-we-are-page">
      {/* Hero Section - Bold & Dramatic with Inline Style */}
      <section 
        className="welcome-hero" 
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(43, 15, 5, 0.85) 0%, rgba(74, 51, 38, 0.7) 50%, rgba(43, 15, 5, 0.85) 100%), url('/images/patnership.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="welcome-hero-overlay"></div>
        <div className="welcome-hero-content">
          <div className="welcome-badge">ABVIG</div>
          <h1>Who We Are</h1>
          <p className="welcome-tagline">Mobilizing Partnerships. Driving Investment. Transforming Africa.</p>
          <div className="welcome-stats">
            <div className="stat-box">
              <span className="stat-number">10+</span>
              <span className="stat-label">Countries</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">$12T</span>
              <span className="stat-label">Investment Opportunities</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">3000+</span>
              <span className="stat-label">Summit Participants</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">56</span>
              <span className="stat-label">Countries Represented</span>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="who-we-are-nav">
        <div className="container">
          <div className="nav-tabs">
            <button 
              className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => scrollToSection('overview')}
            >
              Overview
            </button>
            <button 
              className={`nav-tab ${activeTab === 'vision-mission' ? 'active' : ''}`}
              onClick={() => scrollToSection('vision-mission')}
            >
              Vision & Mission
            </button>
            <button 
              className={`nav-tab ${activeTab === 'values' ? 'active' : ''}`}
              onClick={() => scrollToSection('values')}
            >
              Core Values
            </button>
            <button 
              className={`nav-tab ${activeTab === 'flagship' ? 'active' : ''}`}
              onClick={() => scrollToSection('flagship')}
            >
              Flagship Institutions
            </button>
            <button 
              className={`nav-tab ${activeTab === 'alignment' ? 'active' : ''}`}
              onClick={() => scrollToSection('alignment')}
            >
              Strategic Alignment
            </button>
          </div>
        </div>
      </section>

      {/* Section 1: Corporate Description */}
      <section id="overview" className="who-section corporate-description">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">About ABVIG</span>
            <h2>Corporate Description</h2>
            <div className="gold-line"></div>
          </div>

          <div className="corporate-grid">
            <div className="corporate-text">
              <p>
                <strong>Africa Business Ventures & Investment Group (ABVIG) LLC</strong>, also known as 
                <strong>Africa Business Ventures & Investments International, Nig. Ltd</strong>, is a leading 
                African international institution dedicated to accelerating sustainable infrastructure development, 
                climate resilience, green investment, project preparation, investment promotion, economic diplomacy, 
                regional integration and institutional capacity development across Africa.
              </p>
              <p>
                ABVIG serves as a strategic platform connecting governments, development finance institutions, 
                sovereign wealth funds, multilateral organisations, diplomatic missions, private investors, 
                corporations, academia, development partners and civil society to mobilise investment, prepare 
                bankable projects and promote sustainable economic transformation throughout Africa.
              </p>
              <p>
                A capital mobilization, financial engineering, and investment advisory platform that supports 
                governments, financial institutions, corporations, and project sponsors in structuring bankable, 
                policy-aligned projects and mobilizing long-term capital for infrastructure, trade, climate, 
                industrial, energy, real estate, and sustainable development across Africa.
              </p>
            </div>
            <div className="corporate-image">
              <div className="corporate-image-box" style={{backgroundImage: "url('/images/cooperate.jpg')"}}></div>
            </div>
          </div>

          <div className="corporate-highlights">
            <div className="highlight-item">
              <span className="highlight-icon">🏗️</span>
              <h4>Infrastructure Development</h4>
              <p>Accelerating sustainable infrastructure across Africa</p>
            </div>
            <div className="highlight-item">
              <span className="highlight-icon">🌿</span>
              <h4>Climate Resilience</h4>
              <p>Driving climate action and green investment</p>
            </div>
            <div className="highlight-item">
              <span className="highlight-icon">💰</span>
              <h4>Investment Facilitation</h4>
              <p>Mobilizing capital for transformative projects</p>
            </div>
            <div className="highlight-item">
              <span className="highlight-icon">🤝</span>
              <h4>Economic Diplomacy</h4>
              <p>Building strategic international partnerships</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Vision & Mission */}
      <section id="vision-mission" className="who-section vision-mission-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Direction</span>
            <h2>Vision & Mission</h2>
            <div className="gold-line"></div>
          </div>

          <div className="vision-mission-grid">
            <div className="vision-card">
              <div className="vision-icon">👁️</div>
              <h3>Our Vision</h3>
              <p>
                To become Africa's leading institution for infrastructure development, climate leadership, 
                sustainable investment, project preparation, economic diplomacy and institutional excellence, 
                driving transformational growth and prosperity across Africa and beyond by 2050.
              </p>
            </div>
            <div className="mission-card">
              <div className="mission-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>
                To mobilise strategic partnerships, facilitate sustainable investment, prepare bankable projects, 
                strengthen institutions, advance climate-resilient development, promote innovation and foster 
                inclusive economic transformation through professional excellence, collaborative leadership 
                and international cooperation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Core Values */}
      <section id="values" className="who-section values-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Principles</span>
            <h2>Core Values</h2>
            <div className="gold-line"></div>
          </div>

          <div className="values-grid">
            <div className="value-card">
              <span className="value-number">01</span>
              <h4>Integrity</h4>
              <p>Upholding the highest ethical standards in all our actions.</p>
            </div>
            <div className="value-card">
              <span className="value-number">02</span>
              <h4>Excellence</h4>
              <p>Striving for the highest quality in everything we do.</p>
            </div>
            <div className="value-card">
              <span className="value-number">03</span>
              <h4>Accountability</h4>
              <p>Taking responsibility for our commitments and outcomes.</p>
            </div>
            <div className="value-card">
              <span className="value-number">04</span>
              <h4>Transparency</h4>
              <p>Operating with openness and clear communication.</p>
            </div>
            <div className="value-card">
              <span className="value-number">05</span>
              <h4>Innovation</h4>
              <p>Embracing creative solutions to complex challenges.</p>
            </div>
            <div className="value-card">
              <span className="value-number">06</span>
              <h4>Sustainability</h4>
              <p>Ensuring long-term environmental, social and economic viability.</p>
            </div>
            <div className="value-card">
              <span className="value-number">07</span>
              <h4>Professionalism</h4>
              <p>Maintaining the highest standards of competence and conduct.</p>
            </div>
            <div className="value-card">
              <span className="value-number">08</span>
              <h4>Partnership</h4>
              <p>Building strong, collaborative relationships for shared success.</p>
            </div>
            <div className="value-card">
              <span className="value-number">09</span>
              <h4>Inclusiveness</h4>
              <p>Ensuring everyone has a voice and opportunity to contribute.</p>
            </div>
            <div className="value-card">
              <span className="value-number">10</span>
              <h4>Service to Africa</h4>
              <p>Dedicating our work to the continent's sustainable transformation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Strategic Mandate */}
      <section id="flagship" className="who-section mandate-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Focus</span>
            <h2>Strategic Mandate</h2>
            <div className="gold-line"></div>
          </div>

          <div className="mandate-grid">
            <div className="mandate-item">
              <span className="mandate-icon">🏗️</span>
              <span>Infrastructure Development</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">🌍</span>
              <span>Climate Change & Environmental Sustainability</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">💰</span>
              <span>Green Investment & Climate Finance</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">📊</span>
              <span>Project Preparation & Project Finance</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">🤝</span>
              <span>Public–Private Partnerships (PPPs)</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">📈</span>
              <span>Investment Promotion & Facilitation</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">🌐</span>
              <span>Economic Diplomacy</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">🤝</span>
              <span>Regional & Continental Integration</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">🏭</span>
              <span>Trade & Industrial Development</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">📚</span>
              <span>Executive Education & Leadership Development</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">💡</span>
              <span>Research, Innovation & Knowledge Management</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">🤖</span>
              <span>Digital Transformation & Artificial Intelligence</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">🏛️</span>
              <span>Institutional Strengthening & Capacity Development</span>
            </div>
            <div className="mandate-item">
              <span className="mandate-icon">📋</span>
              <span>Sustainable Development Advisory Services</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Flagship Institutions */}
      <section id="alignment" className="who-section flagship-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Initiatives</span>
            <h2>Flagship Institutions, Programmes & Platforms</h2>
            <div className="gold-line"></div>
          </div>

          <div className="flagship-grid">
            <div className="flagship-card">
              <div className="flagship-icon">🏛️</div>
              <h3>AICIS</h3>
              <p className="flagship-sub">Africa Infrastructure, Climate Change, Security & Green Investment Summit</p>
              <p>ABVIG's flagship continental summit that convenes governments, investors, development partners and the private sector to promote infrastructure development, climate action, investment mobilization and policy dialogue.</p>
            </div>
            <div className="flagship-card">
              <div className="flagship-icon">📚</div>
              <h3>AIICIS</h3>
              <p className="flagship-sub">Africa Institute for Infrastructure, Climate & Investment Studies</p>
              <p>ABVIG's permanent policy institute, executive education academy, research centre, professional certification institution and continental think tank.</p>
            </div>
            <div className="flagship-card">
              <div className="flagship-icon">👥</div>
              <h3>GFVIPEP</h3>
              <p className="flagship-sub">Global Fellowship, Volunteer, Internship, Professional Engagement & Experts Programme</p>
              <p>A structured leadership and professional development programme that nurtures emerging African leaders, professionals, researchers, volunteers, fellows and experts through practical engagement.</p>
            </div>
            <div className="flagship-card">
              <div className="flagship-icon">💼</div>
              <h3>APPIF</h3>
              <p className="flagship-sub">Africa Project Preparation & Investment Facility</p>
              <p>A specialized platform supporting the identification, preparation, structuring, financing and implementation of bankable infrastructure, climate and sustainable development projects across Africa.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Strategic Alignment */}
      <section className="who-section alignment-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Framework</span>
            <h2>Strategic Alignment</h2>
            <div className="gold-line"></div>
          </div>

          <div className="alignment-grid">
            <div className="alignment-item">
              <span className="alignment-icon">🌍</span>
              <p>African Union Agenda 2063</p>
            </div>
            <div className="alignment-item">
              <span className="alignment-icon">🏛️</span>
              <p>UN Sustainable Development Goals (SDGs)</p>
            </div>
            <div className="alignment-item">
              <span className="alignment-icon">📈</span>
              <p>African Continental Free Trade Area (AfCFTA)</p>
            </div>
            <div className="alignment-item">
              <span className="alignment-icon">🌿</span>
              <p>Paris Agreement on Climate Change</p>
            </div>
            <div className="alignment-item">
              <span className="alignment-icon">🏦</span>
              <p>African Development Bank High 5 Priorities</p>
            </div>
            <div className="alignment-item">
              <span className="alignment-icon">⚖️</span>
              <p>Global ESG Principles</p>
            </div>
            <div className="alignment-item">
              <span className="alignment-icon">📋</span>
              <p>National Development Plans of African Countries</p>
            </div>
            <div className="alignment-item">
              <span className="alignment-icon">🌐</span>
              <p>International Treaties & Development Frameworks</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: International Presence */}
      <section className="who-section presence-section">
        <div className="container">
          <div className="presence-content">
            <div className="presence-text">
              <span className="section-badge">Global Reach</span>
              <h2>International Presence</h2>
              <p>
                ABVIG is an international organisation with legal entities, affiliated offices, strategic partnerships 
                and programme operations across multiple jurisdictions.
              </p>
              <p>
                Its institutional presence shall continue to expand through regional offices, country chapters, 
                representative offices, strategic alliances and international partnerships in support of Africa's 
                sustainable development and global engagement.
              </p>
              <div className="presence-countries">
                <span>🇳🇬 Nigeria</span>
                <span>🇬🇭 Ghana</span>
                <span>🇺🇸 United States</span>
                <span>🌍 Expanding Across Africa</span>
              </div>
            </div>
            <div className="presence-image">
              <div className="presence-image-box" style={{backgroundImage: "url('https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600&h=400&fit=crop')"}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8: Corporate Declaration */}
      <section className="who-section declaration-section">
        <div className="container">
          <div className="declaration-content">
            <div className="declaration-icon">📜</div>
            <h2>Corporate Declaration</h2>
            <p>
              Africa Business Ventures & Investment Group (ABVIG) is committed to the highest standards of governance, 
              ethical leadership, transparency, accountability and sustainable development.
            </p>
            <p>
              The Organization shall conduct all its activities in accordance with applicable national and international 
              laws, internationally recognized principles of corporate governance and global best practices while 
              fostering strategic partnerships that promote infrastructure development, climate resilience, investment 
              mobilization, innovation and inclusive economic growth.
            </p>
            <div className="declaration-motto">
              <span>“Mobilising Partnerships • Driving Investment • Transforming Africa.”</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;